import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
    MobileDateTimePicker,
    DateTimePicker,
    renderTimeViewClock,
    DatePicker,
    MobileDatePicker,
} from "@mui/x-date-pickers/";
import { useFormContext, Controller } from "react-hook-form";
import SvgCustomIcon from "../icons/SvgCustomIcon";
import { resolveConfigDisplayLabel, resolveOptionDisplayLabel } from "../../utils/config-localization";
import { shouldDisableFutureDates } from "../../utils/date-field-config";
import { parseFilterDateValue } from "../../utils/filter-date-range.util";

function handleDateFieldChange(onChange, nextValue) {
    if (nextValue == null || nextValue === "") {
        onChange(null);
        return;
    }
    if (parseFilterDateValue(nextValue)) {
        onChange(nextValue);
    }
}

const baseFieldSx = (theme, isDark) => ({
    backgroundColor: isDark ? theme.palette.newColors.lightGray : "#f5f5f5",
    color: isDark ? theme.palette.newColors.gray : "#3C3C3B",
    borderRadius: 1,
    "& label": {
        color: theme.palette.newColors.gray,
    },

    "& .MuiSelect-select": {
        color: isDark ? theme.palette.newColors.gray : "#3C3C3B",
    },
    '& .MuiOutlinedInput-root':{
        color:theme.palette.newColors.gray,
    },
    "& .MuiSelect-icon": {
        color: theme.palette.newColors.gray
    },
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
});

export default function FilterFormElement({ elem, isMobile, form, formList }) {
    const { _ } = useLingui();
    const { control } = useFormContext();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const fieldDisplayLabel = resolveConfigDisplayLabel(elem);

    // ---- Datetime ----
    if (elem.type === "datetime") {
        return (
            <Box width="100%" display="flex" gap={1}>
                {["From", "To"].map((term) => (
                    <FormControl
                        key={`${form.type}-${elem.type}-${elem.label}-${term}`}
                        sx={{ flex: 1 }}
                        margin={elem.margin || "dense"}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Controller
                                name={`${formList[form.number]}.${elem.label}-${term}`}
                                control={control}
                                render={({ field: { value, onChange } }) =>
                                    isMobile ? (
                                        <MobileDateTimePicker
                                            label={`${fieldDisplayLabel} - ${term === "From" ? _(msg`From`) : _(msg`To`)}`}
                                            disableFuture={shouldDisableFutureDates(elem)}
                                            slots={{ openPickerIcon: SvgCustomIcon }}
                                            slotProps={{
                                                openPickerIcon: {
                                                    name: term === "From" ? "dateFrom" : "dateTo",
                                                    color: theme.palette.newColors.gray
                                                },
                                                openPickerButton: { sx: { color: "#E0286C" } },
                                            }}
                                            value={value ?? null}
                                            onChange={(nextValue) => handleDateFieldChange(onChange, nextValue)}
                                            onAccept={onChange}
                                        />
                                    ) : (
                                        <DateTimePicker
                                            label={`${fieldDisplayLabel} - ${term === "From" ? _(msg`From`) : _(msg`To`)}`}
                                            disableFuture={shouldDisableFutureDates(elem)}
                                            value={value ?? null}
                                            slots={{ openPickerIcon: SvgCustomIcon }}
                                            slotProps={{
                                                openPickerIcon: {
                                                    name: term === "From" ? "dateFrom" : "dateTo",
                                                    color: theme.palette.newColors.gray
                                                },
                                                textField: { sx: baseFieldSx(theme, isDark) },
                                                openPickerButton: { sx: { color: "#E0286C" } },
                                            }}
                                            onChange={(nextValue) => handleDateFieldChange(onChange, nextValue)}
                                            onAccept={onChange}
                                            viewRenderers={{
                                                hours: renderTimeViewClock,
                                                minutes: renderTimeViewClock,
                                                seconds: renderTimeViewClock,
                                            }}
                                        />
                                    )
                                }
                            />
                        </LocalizationProvider>
                    </FormControl>
                ))}
            </Box>
        );
    }

    // ---- Date ----
    if (elem.type === "date") {
        return (
            <Box
                width="100%"
                display="flex"
                gap={elem.stack ? 0 : 1}
                flexDirection={elem.stack ? "column" : "row"}
            >
                {["From", "To"].map((term) => (
                    <FormControl
                        key={`crash-${elem.type}-${elem.label}-${term}`}
                        sx={{ flex: 1 }}
                        margin={elem.margin || "dense"}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Controller
                                name={`${formList ? formList[form.number] : ""}.${
                                    elem.label
                                }-${term}`}
                                control={control}
                                render={({ field: { value, onChange } }) =>
                                    isMobile ? (
                                        <MobileDatePicker
                                            label={`${fieldDisplayLabel} - ${term === "From" ? _(msg`From`) : _(msg`To`)}`}
                                            disableFuture={shouldDisableFutureDates(elem)}
                                            value={value ?? null}
                                            slots={{ openPickerIcon: SvgCustomIcon }}
                                            slotProps={{
                                                openPickerIcon: {
                                                    name: term === "From" ? "dateFrom" : "dateTo",
                                                    color: theme.palette.newColors.gray
                                                },
                                                textField: { sx: baseFieldSx(theme, isDark) },
                                                openPickerButton: { sx: { color: "#E0286C" } },
                                            }}
                                            onChange={(nextValue) => handleDateFieldChange(onChange, nextValue)}
                                            onAccept={onChange}
                                        />
                                    ) : (
                                        <DatePicker
                                            label={`${fieldDisplayLabel} - ${term === "From" ? _(msg`From`) : _(msg`To`)}`}
                                            disableFuture={shouldDisableFutureDates(elem)}
                                            value={value ?? null}
                                            slots={{ openPickerIcon: SvgCustomIcon }}
                                            slotProps={{
                                                openPickerIcon: {
                                                    name: term === "From" ? "dateFrom" : "dateTo",
                                                    color: theme.palette.newColors.gray
                                                },
                                                textField: { sx: baseFieldSx(theme, isDark) },
                                                openPickerButton: { sx: { color: "#E0286C" } },
                                            }}
                                            onChange={(nextValue) => handleDateFieldChange(onChange, nextValue)}
                                            onAccept={onChange}
                                        />
                                    )
                                }
                            />
                        </LocalizationProvider>
                    </FormControl>
                ))}
            </Box>
        );
    }

    // ---- Select ----
    if (elem.type === "select") {
        return (
            <Box width="100%">
                <FormControl
                    fullWidth
                    key={`${elem.type}-${elem.label}`}
                    margin={elem.margin || "dense"}
                    sx={{
                        "& .MuiInputLabel-root": {
                            color: isDark ? theme.palette.newColors.gray : "#3C3C3B",
                        },
                    }}
                >
                    <InputLabel id={`select-${elem.label}`}>{fieldDisplayLabel}</InputLabel>
                    <Controller
                        name={`${formList ? formList[form.number] : ""}.${elem.label}`}
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                labelId={`select-${elem.label}`}
                                id={`select-${elem.label}`}
                                label={fieldDisplayLabel}
                                value={field.value || ''}
                                onChange={field.onChange}
                                startAdornment={
                                    elem.prefix && (
                                        <InputAdornment>{elem.prefix}&nbsp;</InputAdornment>
                                    )
                                }
                                sx={{
                                    ...baseFieldSx(theme, isDark),
                                    "&:hover": { backgroundColor: "inherit" }, // no hover white
                                }}
                                MenuProps={{
                                    sx: {
                                        "& .MuiPaper-root": {
                                            backgroundColor: isDark
                                                ? theme.palette.newColors.lightGray
                                                : "#f5f5f5",
                                            color: isDark
                                                ? theme.palette.newColors.gray
                                                : "#3C3C3B",
                                            boxShadow: isDark
                                                ? "0 2px 8px rgba(0,0,0,0.4)"
                                                : "0 2px 6px rgba(0,0,0,0.15)",
                                        },
                                    },
                                }}
                            >
                                {elem.options.map((opt) => {
                                    if (
                                        field.name.includes("Nationality") ||
                                        field.name.includes("country")
                                    ) {
                                        return (
                                            <MenuItem key={opt.label} value={opt.value}>
                                                <Box display="flex">
                                                    <Box
                                                        height="1.5rem"
                                                        border="1px solid #c4c4c4"
                                                        borderRadius={1}
                                                        sx={{
                                                            aspectRatio: "4/3",
                                                            backgroundImage: `url('${opt.flag}')`,
                                                            backgroundSize: "cover",
                                                            backgroundPosition: "center center",
                                                        }}
                                                    />
                                                    <Typography ml={3}>{resolveOptionDisplayLabel(opt)}</Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    } else {
                                        return opt.value && opt.value % 100 === 0 ? (
                                            <ListSubheader key={`${opt.label}-sub`}>
                                                {resolveOptionDisplayLabel(opt)}
                                            </ListSubheader>
                                        ) : (
                                            <MenuItem key={opt.label} value={opt.value}>
                                                {resolveOptionDisplayLabel(opt)}
                                            </MenuItem>
                                        );
                                    }
                                })}
                            </Select>
                        )}
                    />
                </FormControl>
            </Box>
        );
    }

    // ---- TextField ----
    return (
        <FormControl
            fullWidth
            key={`${elem.type}-${elem.label}`}
            margin={elem.margin || "dense"}
        >
            <Controller
                name={`${formList ? formList[form.number] : ""}.${elem.label}`}
                control={control}
                render={({ field }) => (
                    <TextField
                        fullWidth
                        required={elem.isRequired}
                        id={elem.label}
                        label={elem.label}
                        sx={baseFieldSx(theme, isDark)}
                        {...field}
                        value={field.value ?? ''}
                    />
                )}
            />
        </FormControl>
    );
}
